<template>
  <div class="faq-section">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      title="Frequently Asked Questions"
      height="700px"
    />

    <!-- Wavy Transition -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <!-- FAQ Section -->
    <div class="container-fluid">
      <h2>{{ $t('faqs.title') }}</h2>
      <p>{{ $t('faqs.description') }}</p>

      <div class="faq-list">
        <div v-if="loading" class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>
        <div v-else-if="error" class="error-message">
          {{ error }}
        </div>
        <div v-else v-for="(faq, index) in faqs" :key="faq.id" class="faq-item">
          <button class="faq-question" @click="toggleFaq(index)">
            {{ locale === 'en' ? faq.question_en : faq.question_ar }}
            <span :class="{'rotate': activeIndex === index}">▼</span>
          </button>
          <div v-if="activeIndex === index" class="faq-answer" v-html="locale === 'en' ? faq.answer_en : faq.answer_ar">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Banner from "@/components/Banner.vue";
import axiosInstance from "@/plugins/axios";
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const faqs = ref([]);
const activeIndex = ref(null);
const loading = ref(true);
const error = ref(null);

const fetchFaqs = async () => {
  try {
    loading.value = true;
    const response = await axiosInstance.get('/app-faq');
    
    // Log the response to debug
    console.log('FAQ Response:', response);
    
    // Use the correct path to access the data
    faqs.value = response.data;
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    // If the error has a response, log more details
    if (error.response) {
      console.error('Response Error:', error.response.data);
    }
    error.value = 'Failed to load FAQs. Please try again later.';
  } finally {
    loading.value = false;
  }
};

const toggleFaq = (index) => {
  activeIndex.value = activeIndex.value === index ? null : index;
};

onMounted(() => {
  fetchFaqs();
});
</script>

<style scoped>
/* 📌 FAQ Page Styling */
.faq-section {
  position: relative;
  background: white;
  margin-top: 700px;
}

/* 📌 Wave Effect */
.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

/* 📌 FAQ Container */
.container-fluid {
  position: relative;
  width: 100%;
  padding: 30px 50px;
  background: white;
  margin-top: -1px;
}

.container-fluid h2 {
  color: #0056b3;
  margin-top: 20px;
}

/* 📌 FAQ List */
.faq-list {
  margin-top: 30px;
}

.faq-item {
  margin-bottom: 15px;
}

.faq-question {
  width: 100%;
  background: #f4f4f4;
  border: none;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  cursor: pointer;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.faq-question:hover {
  background: #e2e2e2;
}

.faq-answer {
  padding: 15px;
  background: #f9f9f9;
  border-left: 3px solid #0056b3;
  margin-top: 5px;
  border-radius: 5px;
}

span {
  transition: transform 0.3s ease-in-out;
}

.rotate {
  transform: rotate(180deg);
}
.loading-spinner {
  text-align: center;
  padding: 20px;
  color: #1268B1;
  font-size: 1.2rem;
}

.error-message {
  text-align: center;
  padding: 20px;
  color: #dc3545;
  background: #f8d7da;
  border-radius: 5px;
}

/* Add some styles to enhance link visibility in answers */
.faq-answer a {
  color: #1268B1;
  text-decoration: none;
  font-weight: bold;
}

.faq-answer a:hover {
  text-decoration: underline;
}
/* 📌 Wave Animation */
@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

/* 📌 Responsive Styling */
@media (max-width: 768px) {
  .wave-container {
    top: -150px;
    height: 170px;
  }

  .container-fluid {
    padding: 20px 30px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }

  .container-fluid {
    padding: 15px 20px;
  }
}

/* 📌 RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}
</style>
